import React, { useEffect, useState, Fragment, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Tabs,
    Tab,
    Button,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    NetworkCheck,
    Token as TokenIcon,
    MonitorHeart,
    Warning as WarningIcon,
    CheckCircle,
    Error as ErrorIcon,
    Info as InfoIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    FileDownload as FileDownloadIcon,
    TrendingUp as TrendingUpIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Refresh as RefreshIcon,
    KeyOff as KeyOffIcon,
    Edit as EditIcon,
    Extension as ExtensionIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import AddVNeIDAccountModal from './AddVNeIDAccountModal';
import AssignSdkModal from './AssignSdkModal';
import Tab3VerificationQueries from '../VNeIDVerificationQueries/Tab3VerificationQueries';
import Tab4SdkApiManagement from './Tab4SdkApiManagement';
import Tab5QueryMonitoring from './Tab5QueryMonitoring';
import vneidAccountService from '../../../services/vneidAccountService';
import unitService from '../../../services/unitService';
import {
    setActiveTab,
    pingConnection,
    refreshToken,
    startMonitoring,
    stopMonitoring,
    checkInterruptions,
    fetchRecentEvents,
    fetchAccounts,
    fetchStatistics,
    createAccount,
    updateAccount,
    deleteAccount,
    bulkDeleteAccounts,
    toggleAccountStatus,
    regenerateApiKey,
    revokeApiKey,
    assignSdk,
    setAccountsPage,
    setAccountsPageSize,
    setAccountsSortColumn,
    setAccountsSortDirection,
    setAccountsFilterUnitName,
    setAccountsFilterAccountName,
    setAccountsFilterStatus,
    setAccountsSelectedIds,
    clearMessages,
} from './slice';

function TabPanel({ children, value, index, ...other }) {
    return (
        <>
            <style>{`
                .tab-panel-scroll::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
            <div
                role="tabpanel"
                hidden={value !== index}
                style={{
                    height: value === index ? 'calc(100vh - 200px)' : '0',
                    overflow: value === index ? 'auto' : 'hidden',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
                className="tab-panel-scroll"
                {...other}
            >
                {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
            </div>
        </>
    );
}

export default function VNeIDStatistics() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { activeTab, connectionStatus, recentEvents, accounts, loading, errors, success } = useSelector((state) => state.vneidStatistics);

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showAssignSdkDialog, setShowAssignSdkDialog] = useState(false);
    const [accountToEdit, setAccountToEdit] = useState(null);
    const [accountToAssignSdk, setAccountToAssignSdk] = useState(null);
    const [units, setUnits] = useState([]);

    // Refs to track tab 2 loading state and filter changes
    const tab2LoadedRef = useRef(false);
    const prevFiltersRef = useRef('');

    // Fetch accounts and statistics when Tab 2 is active (only on first load or filter/pagination change)
    useEffect(() => {
        if (activeTab === 1) {
            const currentFilters = JSON.stringify({
                page: accounts.page,
                pageSize: accounts.pageSize,
                sortColumn: accounts.sortColumn,
                sortDirection: accounts.sortDirection,
                filters: accounts.filters
            });
            
            // Fetch if: first time loading OR filters/pagination changed
            if (!tab2LoadedRef.current || prevFiltersRef.current !== currentFilters) {
                dispatch(fetchAccounts());
                dispatch(fetchStatistics());
                tab2LoadedRef.current = true;
                prevFiltersRef.current = currentFilters;
            }
        }
    }, [activeTab, dispatch, accounts.page, accounts.pageSize, accounts.sortColumn, accounts.sortDirection, accounts.filters]);

    useEffect(() => {
        dispatch(fetchRecentEvents());
        // Fetch connection status on mount
        dispatch(pingConnection());
    }, [dispatch]);

    // Fetch units for the dropdown using init endpoint
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await unitService.getUnitInit();
                setUnits(response.units || []);
            } catch (error) {
                console.error('Error fetching units:', error);
            }
        };
        fetchUnits();
    }, []);

    useEffect(() => {
        return () => {
            dispatch(stopMonitoring());
        };
    }, [dispatch]);

    const handleTabChange = (event, newValue) => {
        dispatch(setActiveTab(newValue));
    };

    const handlePing = () => {
        dispatch(pingConnection());
    };

    const handleRefreshToken = () => {
        dispatch(refreshToken());
    };

    const handleMonitoring = () => {
        if (loading.monitoring) {
            dispatch(stopMonitoring());
        } else {
            dispatch(startMonitoring());
        }
    };

    const handleCheckInterruptions = () => {
        dispatch(checkInterruptions());
    };

    const getLastCheckedText = () => {
        if (!connectionStatus.lastChecked) return '';

        const seconds = Math.floor((Date.now() - new Date(connectionStatus.lastChecked)) / 1000);

        if (seconds < 60) return `${seconds} ${t('vneid.stats.secondsAgo')}`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)} ${t('vneid.stats.minutesAgo')}`;
        return `${Math.floor(seconds / 3600)} ${t('vneid.stats.hoursAgo')}`;
    };

    // Tab 2: VNeID Accounts Management handlers
    const handleAddAccount = () => {
        setShowAddDialog(true);
    };

    const handleEditAccount = (account) => {
        setAccountToEdit(account);
        setShowEditDialog(true);
    };

    const handleDeleteAccount = (account) => {
        if (confirm(t('vneid.accounts.deleteDialog.message', { accountName: account.accountName }))) {
            dispatch(deleteAccount(account.id));
        }
    };

    const handleBulkDelete = () => {
        if (confirm(t('vneid.accounts.bulkDeleteDialog.message', { count: accounts.selectedIds.length }))) {
            dispatch(bulkDeleteAccounts(accounts.selectedIds));
        }
    };

    const handleToggleStatus = (account) => {
        const action = account.status ? t('vneid.accounts.toggleStatusDialog.lock') : t('vneid.accounts.toggleStatusDialog.unlock');
        if (confirm(t('vneid.accounts.toggleStatusDialog.message', { action, accountName: account.accountName }))) {
            dispatch(toggleAccountStatus(account.id));
        }
    };

    const handleRegenerateKey = (account) => {
        if (confirm(t('vneid.accounts.regenerateKeyDialog.message', { accountName: account.accountName }))) {
            dispatch(regenerateApiKey(account.id));
        }
    };

    const handleRevokeKey = (account) => {
        if (confirm(t('vneid.accounts.revokeKeyDialog.message', { accountName: account.accountName }))) {
            dispatch(revokeApiKey(account.id));
        }
    };

    const handleAssignSdk = (account) => {
        setAccountToAssignSdk(account);
        setShowAssignSdkDialog(true);
    };

    const handleAssignSdkConfirm = (data) => {
        dispatch(assignSdk(data));
        setShowAssignSdkDialog(false);
        setAccountToAssignSdk(null);
    };

    const handleExportReport = async () => {
        try {
            // Determine report type based on status filter
            let reportType = 'statistics'; // Default: all accounts
            if (accounts.filters.status === true) {
                reportType = 'active';
            } else if (accounts.filters.status === false) {
                reportType = 'locked';
            }

            // Get current filter values
            const reportRequest = {
                reportType: reportType,
                fromDate: null,
                toDate: null,
                exportFormat: 'excel' // Default to Excel
            };

            // Export to Excel
            await vneidAccountService.exportAccountsReportToExcel(reportRequest);
        } catch (error) {
            console.error('Error exporting report:', error);
            alert(t('vneid.accounts.exportError') || 'Failed to export report');
        }
    };

    const handleAddConfirm = (accountData) => {
        dispatch(createAccount(accountData));
    };

    const handleEditConfirm = (accountData) => {
        dispatch(updateAccount(accountData));
    };

    // Get statistics from state (fetched from API)
    const statistics = accounts.statistics;
    const activeAccountsCount = statistics.activeAccounts || 0;
    const lockedAccountsCount = statistics.lockedAccounts || 0;
    const totalQueriesToday = statistics.queriesToday || 0;
    const usagePercentage = statistics.queriesTodayPercent || 0;
    const activeAccountsChange = Math.round(statistics.activeAccountsChangePercent || 0);

    // Table columns for Tab 2
    const accountsColumns = [
        { key: 'unitName', label: t('vneid.accounts.table.unitName') },
        { key: 'unitCodeId', label: t('vneid.accounts.table.unitCodeId') },
        { key: 'accountName', label: t('vneid.accounts.table.accountName') },
        { key: 'apiKey', label: t('vneid.accounts.table.apiKey'), render: (value) => `${value.substring(0, 20)}...` },
        {
            key: 'status',
            label: t('vneid.accounts.table.status'),
            render: (value) => (
                <Chip label={value ? t('vneid.accounts.active') : t('vneid.accounts.locked')} color={value ? 'success' : 'error'} size="small" />
            ),
        },
        { key: 'accessCount', label: t('vneid.accounts.table.accessCount') },
        {
            key: 'lastAccessDate',
            label: t('vneid.accounts.table.lastAccessDate'),
            render: (value) => (value ? new Date(value).toLocaleString('vi-VN') : '-'),
        },
        {
            key: 'createdOn',
            label: t('vneid.accounts.table.createdOn'),
            render: (value) => new Date(value).toLocaleDateString('vi-VN'),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {activeTab === 0 && t('vneid.stats.tab1')}
                        {activeTab === 1 && t('vneid.stats.tab2')}
                        {activeTab === 2 && t('vneid.stats.tab3')}
                        {activeTab === 3 && t('vneid.stats.tab4')}
                        {activeTab === 4 && t('vneid.stats.tab5')}
                    </Typography>
                    <Chip
                        label={connectionStatus.status === 'ONLINE' ? t('vneid.stats.online') : t('vneid.stats.offline')}
                        color={connectionStatus.status === 'ONLINE' ? 'success' : 'error'}
                        size="small"
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {t('vneid.stats.lastUpdate')}: {getLastCheckedText()}
                </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label={t('vneid.stats.tab1')} />
                    <Tab label={t('vneid.stats.tab2')} />
                    <Tab label={t('vneid.stats.tab3')} />
                    <Tab label={t('vneid.stats.tab4')} />
                    <Tab label={t('vneid.stats.tab5')} />
                </Tabs>
            </Box>

            {/* Tab 1: Connection Overview */}
            <TabPanel value={activeTab} index={0}>
                {/* Stats Cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                        },
                        gap: 3,
                        width: '100%',
                        mb: 4,
                    }}
                >
                    {/* Connection Status */}
                    <Card
                        sx={{
                            background:
                                connectionStatus.status === 'ONLINE'
                                    ? 'linear-gradient(135deg, #2d8659 0%, #17a2b8 100%)'
                                    : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                            color: 'white',
                            height: '100%',
                        }}
                    >
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                {connectionStatus.status}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.stats.connectionStatus')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t('vneid.stats.lastChecked')}: {getLastCheckedText()}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Uptime */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#2d8659' }}>
                                {connectionStatus.uptime}%
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.stats.uptime24h')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {connectionStatus.downtimeMinutes} {t('vneid.stats.minutesDowntime')}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Response Time */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#17a2b8' }}>
                                {connectionStatus.avgResponseTime}s
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.stats.avgResponseTime')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {connectionStatus.recentRequests} {t('vneid.stats.recentRequests')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<NetworkCheck />}
                            onClick={handlePing}
                            disabled={loading.ping}
                            sx={{
                                bgcolor: '#1976d2',
                                '&:hover': { bgcolor: '#115293' },
                            }}
                        >
                            {loading.ping ? t('vneid.stats.pinging') : t('vneid.stats.pingButton')}
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<TokenIcon />}
                            onClick={handleRefreshToken}
                            disabled={loading.refreshToken}
                            sx={{
                                bgcolor: '#2e7d32',
                                '&:hover': { bgcolor: '#1b5e20' },
                            }}
                        >
                            {loading.refreshToken ? t('vneid.stats.refreshing') : t('vneid.stats.refreshTokenButton')}
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<MonitorHeart />}
                            onClick={handleMonitoring}
                            sx={{
                                bgcolor: loading.monitoring ? '#d32f2f' : '#0288d1',
                                '&:hover': { bgcolor: loading.monitoring ? '#c62828' : '#01579b' },
                            }}
                        >
                            {loading.monitoring ? t('vneid.stats.stopMonitoring') : t('vneid.stats.monitorButton')}
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<WarningIcon />}
                            onClick={handleCheckInterruptions}
                            disabled={loading.checkInterruptions}
                            sx={{
                                bgcolor: '#ed6c02',
                                '&:hover': { bgcolor: '#e65100' },
                            }}
                        >
                            {loading.checkInterruptions ? t('vneid.stats.checking') : t('vneid.stats.checkInterruptionsButton')}
                        </Button>
                    </Grid>
                </Grid>

                {/* Success/Error Messages for Actions */}
                {success.ping && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {success.ping}
                    </Alert>
                )}
                {errors.ping && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {errors.ping}
                    </Alert>
                )}
                {success.refreshToken && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {success.refreshToken}
                    </Alert>
                )}
                {errors.refreshToken && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {errors.refreshToken}
                    </Alert>
                )}

                {/* Recent Events */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <InfoIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t('vneid.stats.recentEvents')}
                            </Typography>
                        </Box>

                        {recentEvents.length > 0 ? (
                            <List>
                                {recentEvents.map((event, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                                            <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                                                {event.type === 'success' && <CheckCircle color="success" />}
                                                {event.type === 'error' && <ErrorIcon color="error" />}
                                                {event.type === 'warning' && <WarningIcon color="warning" />}
                                                {event.type === 'info' && <InfoIcon color="info" />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {event.title}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <React.Fragment>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {event.description}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(event.timestamp).toLocaleString('vi-VN')}
                                                        </Typography>
                                                    </React.Fragment>
                                                }
                                            />
                                        </ListItem>
                                        {index < recentEvents.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                                {t('vneid.stats.noEvents')}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Tab 2: VNeID Accounts Management */}
            <TabPanel value={activeTab} index={1}>
                {/* Statistics Cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(4, 1fr)',
                        },
                        gap: 3,
                        width: '100%',
                        mb: 4,
                    }}
                >
                    {/* Active Accounts */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#2d8659' }}>
                                {activeAccountsCount}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.accounts.activeAccounts')}
                            </Typography>
                            {activeAccountsChange !== 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TrendingUpIcon sx={{ fontSize: 16, color: activeAccountsChange > 0 ? '#2d8659' : '#d32f2f' }} />
                                    <Typography variant="body2" color={activeAccountsChange > 0 ? 'success.main' : 'error.main'}>
                                        {activeAccountsChange > 0 ? '+' : ''}{activeAccountsChange}% {t('vneid.accounts.thisMonth')}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Locked Accounts */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#f0a531' }}>
                                {lockedAccountsCount}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.accounts.lockedAccounts')}
                            </Typography>
                            <Typography variant="body2" color="warning.main">
                                {t('vneid.accounts.needsReview')}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Queries Today */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#17a2b8' }}>
                                {totalQueriesToday.toLocaleString()}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.accounts.queriesToday')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {usagePercentage.toFixed(1)}% {t('vneid.accounts.ofLimit')}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Usage Limit */}
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
                                {usagePercentage.toFixed(1)}%
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t('vneid.accounts.usageLimit')}
                            </Typography>
                            <Typography variant="body2" color="success.main">
                                {t('vneid.accounts.withinLimit')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons and Filters - Single Row */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddAccount}
                        id="addAccount"
                        name="addAccount"
                        data-testid="vneid-accounts-button-addAccount"
                    >
                        {t('vneid.accounts.addAccount')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                        disabled={accounts.selectedIds.length === 0}
                        id="deleteSelected"
                        name="deleteSelected"
                        data-testid="vneid-accounts-button-deleteSelected"
                    >
                        {t('vneid.accounts.deleteSelected')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportReport}
                        id="exportReport"
                        name="exportReport"
                        data-testid="vneid-accounts-button-exportReport"
                    >
                        {t('vneid.accounts.exportReport')}
                    </Button>
                    <TextField
                        label={t('vneid.accounts.filterByUnit')}
                        value={accounts.filters.unitName}
                        onChange={(e) => dispatch(setAccountsFilterUnitName(e.target.value))}
                        size="small"
                        sx={{ minWidth: 200 }}
                        inputProps={{
                            id: 'filterUnitName',
                            name: 'filterUnitName',
                            'data-testid': 'vneid-accounts-input-filterUnitName',
                        }}
                    />
                    <TextField
                        label={t('vneid.accounts.filterByAccountName')}
                        value={accounts.filters.accountName}
                        onChange={(e) => dispatch(setAccountsFilterAccountName(e.target.value))}
                        size="small"
                        sx={{ minWidth: 200 }}
                        inputProps={{
                            id: 'filterAccountName',
                            name: 'filterAccountName',
                            'data-testid': 'vneid-accounts-input-filterAccountName',
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="filter-status-label">{t('vneid.accounts.filterByStatus')}</InputLabel>
                        <Select
                            labelId="filter-status-label"
                            value={accounts.filters.status === null ? '' : accounts.filters.status}
                            onChange={(e) => dispatch(setAccountsFilterStatus(e.target.value === '' ? null : e.target.value))}
                            label={t('vneid.accounts.filterByStatus')}
                            inputProps={{
                                id: 'filterStatus',
                                name: 'filterStatus',
                                'data-testid': 'vneid-accounts-select-filterStatus',
                            }}
                        >
                            <MenuItem value="">{t('vneid.accounts.allStatus')}</MenuItem>
                            <MenuItem value={true}>{t('vneid.accounts.active')}</MenuItem>
                            <MenuItem value={false}>{t('vneid.accounts.locked')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Success/Error Messages */}
                {success.accounts && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {success.accounts}
                    </Alert>
                )}
                {errors.accounts && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearMessages())}>
                        {errors.accounts}
                    </Alert>
                )}

                {/* Accounts Table */}
                <Table
                    id="vneid-accounts-table"
                    columns={accountsColumns}
                    data={accounts.list}
                    selectable={true}
                    selectedIds={accounts.selectedIds}
                    onSelectionChange={(ids) => dispatch(setAccountsSelectedIds(ids))}
                    loading={loading.fetchAccounts}
                    pagination={true}
                    page={accounts.page}
                    pageSize={accounts.pageSize}
                    totalItems={accounts.total}
                    onPageChange={(newPage) => dispatch(setAccountsPage(newPage))}
                    onPageSizeChange={(newSize) => dispatch(setAccountsPageSize(newSize))}
                    sortable={true}
                    sortColumn={accounts.sortColumn}
                    sortDirection={accounts.sortDirection}
                    onSortChange={(col, dir) => {
                        dispatch(setAccountsSortColumn(col));
                        dispatch(setAccountsSortDirection(dir));
                    }}
                    renderActions={(row) => (
                        <>
                            <Tooltip title={row.status ? t('vneid.accounts.toggleStatusDialog.lock') : t('vneid.accounts.toggleStatusDialog.unlock')}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleToggleStatus(row)}
                                    id={`toggleStatus-${row.id}`}
                                    name={`toggleStatus-${row.id}`}
                                    data-testid={`vneid-accounts-button-toggleStatus-${row.id}`}
                                >
                                    {row.status ? <LockIcon /> : <LockOpenIcon />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('vneid.accounts.regenerateKeyDialog.title')}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRegenerateKey(row)}
                                    id={`regenerateKey-${row.id}`}
                                    name={`regenerateKey-${row.id}`}
                                    data-testid={`vneid-accounts-button-regenerateKey-${row.id}`}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('vneid.accounts.revokeKeyDialog.title')}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRevokeKey(row)}
                                    id={`revokeKey-${row.id}`}
                                    name={`revokeKey-${row.id}`}
                                    data-testid={`vneid-accounts-button-revokeKey-${row.id}`}
                                >
                                    <KeyOffIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('vneid.accounts.assignSdkDialog.title')}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleAssignSdk(row)}
                                    id={`assignSdk-${row.id}`}
                                    name={`assignSdk-${row.id}`}
                                    data-testid={`vneid-accounts-button-assignSdk-${row.id}`}
                                >
                                    <ExtensionIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.edit')}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleEditAccount(row)}
                                    id={`edit-${row.id}`}
                                    name={`edit-${row.id}`}
                                    data-testid={`vneid-accounts-button-edit-${row.id}`}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteAccount(row)}
                                    id={`delete-${row.id}`}
                                    name={`delete-${row.id}`}
                                    data-testid={`vneid-accounts-button-delete-${row.id}`}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                />
            </TabPanel>

            {/* Tab 3: VNeID Verification Queries */}
            <TabPanel value={activeTab} index={2}>
                <Tab3VerificationQueries />
            </TabPanel>

            {/* Tab 4: SDK/API Management (frontend-only visuals) */}
            <TabPanel value={activeTab} index={3}>
                <Tab4SdkApiManagement />
            </TabPanel>

            {/* Tab 5: Query Monitoring */}
            <TabPanel value={activeTab} index={4}>
                <Tab5QueryMonitoring />
            </TabPanel>

            {/* Add/Edit Account Modals */}
            <AddVNeIDAccountModal
                open={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                onConfirm={handleAddConfirm}
                editMode={false}
                loading={loading.createAccount}
                units={units}
            />

            <AddVNeIDAccountModal
                open={showEditDialog}
                onClose={() => setShowEditDialog(false)}
                onConfirm={handleEditConfirm}
                accountData={accountToEdit}
                editMode={true}
                loading={loading.updateAccount}
                units={units}
            />

            <AssignSdkModal
                open={showAssignSdkDialog}
                onClose={() => {
                    setShowAssignSdkDialog(false);
                    setAccountToAssignSdk(null);
                }}
                onConfirm={handleAssignSdkConfirm}
                account={accountToAssignSdk}
                loading={loading.assignSdk}
            />
        </Box>
    );
}
